import { useApplicationConfigStore } from '~/store/application-config'
import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import { ApplicationConfigService } from '~/libs/store-services/application-config-service'

function loadAnswersFromParams(query) {
    const applicationConfigService = new ApplicationConfigService() // Loading without $axios; Only need localStorage

    const answers = {
      'financeAmount': query.amountSeeking || query.financeAmount
    }
    const validators = {
      'financeAmount': /^\d+$/
    }

    for (const key in answers) {
      let answerEmpty = answers[key] === null || answers[key] === undefined
      if (answerEmpty) {
        delete answers[key]
      }

      if (!answerEmpty && validators[key] && !validators[key].test(answers[key])) {
        console.warn('Not saving data from params because it doesn\'t match validation.', { [key]: answers[key] })
        delete answers[key]
      }
    }

    if (Object.keys(answers).length <= 0) {
      return
    }

    applicationConfigService.mergeAnswersToStorage(answers)
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  const identifier = to.params?.identifier
  const toSlug = to.params?.slug
  const fromSlug = from?.params?.slug

  if (import.meta.client) {
    loadAnswersFromParams(to.query)
  }

  if (!identifier) {
    throw createError({
      statusCode: 500,
      statusMessage: `Invalid Application Config Identifier: ${identifier}`,
      fatal: true
    })
  }
  const nuxtApp = useNuxtApp()
  const { $pinia } = nuxtApp
  const { getByIdentifierAndSlug, getModuleFromSlug, setIsBackEvent } = useApplicationConfigStore($pinia)
  const { getBorrower } = useBorrowerStore($pinia)
  const { isLoggedIn, authUser, isEmbedded } = useRootStore($pinia)

  // get Application Config
  const applicationConfig = await getByIdentifierAndSlug({ identifier, slug: toSlug })
  if (!applicationConfig) {
    throw createError({
      statusCode: 500,
      statusMessage: `Failed to fetch Application Config. Identifier: ${identifier}, Slug: ${toSlug}`,
      fatal: true
    })
  }

  const moduleUrlPrefix = applicationConfig.moduleUrlPrefix

  // make sure Application Config has some Application Modules
  if (!applicationConfig.modules?.length) {
    throw createError({
      statusCode: 500,
      statusMessage: 'Application Config has no Application Modules',
      fatal: true
    })
  }

  // make sure all Application Modules have a slug
  if (applicationConfig.modules.some(x => !x.urlSlug)) {
    throw createError({
      statusCode: 500,
      statusMessage: 'Application Modules missing urlSlugs',
      fatal: true
    })
  }

  const firstSlug = applicationConfig.modules[0]?.urlSlug

  // if no slug, navigate to first Application Module
  if (!toSlug) {
    return navigateTo(`${moduleUrlPrefix}${identifier}-app/${firstSlug}`, { replace: true })
  }

  const applicationModule = applicationConfig.modules.find(x => x.urlSlug === toSlug)
  const fromModule = getModuleFromSlug(fromSlug)

  // otherwise, find the Application Module with matching slug
  if (!applicationModule) {
    // if module not found, use the next module slug if available, defaulting to the first module slug
    const redirectSlug = fromModule?.navigationSlugs?.next ?? firstSlug
    
    // Our experiment redirects should be extensions of the module slug, so if redirect contains the original slug, we can ignore the warning
    if (!redirectSlug.includes(toSlug)) {
      const moduleSlugs = applicationConfig.modules.map(module => module.urlSlug)
      log.warning('No Application Module found for provided slug', { identifier, toSlug, from: from.fullPath, redirectSlug, moduleSlugs })
    }
    return navigateTo(`${moduleUrlPrefix}${identifier}-app/${redirectSlug}`, { replace: true })
  }

  const borrower = await getBorrower()
  const isAuthRequired = applicationModule?.authRequired === true

  const isUserWithoutPassword = isLoggedIn && !authUser?.has_set_password

  if (!isEmbedded && isUserWithoutPassword && isAuthRequired) {
    return navigateTo(`/newpass?r=${encodeURIComponent(`${moduleUrlPrefix}${identifier}-app/${toSlug}`)}`)
  }

  if ((!isLoggedIn || !borrower) && isAuthRequired) {
    return navigateTo(`/login?r=${moduleUrlPrefix}${identifier}-app/${toSlug}`, { replace: true })
  }

  const backNavSlug = fromModule?.navigationSlugs?.back
  const _isBackEvent = backNavSlug === toSlug || (toSlug === 'basic-info' && from?.path === '/owner-info')
  setIsBackEvent(_isBackEvent)

  const isPageType = applicationModule.type === 'PAGE'
  const shouldRedirectToBasicInfo = isPageType && toSlug === 'owner-info' && _isBackEvent

  if (shouldRedirectToBasicInfo) {
    return navigateTo(`${moduleUrlPrefix}${identifier}-app/basic-info`)
  }

  if (isPageType) {
    return navigateTo(`/${toSlug}`)
  }

  to.meta.layout = isEmbedded ? 'embedded' : 'marketplace'

  if (to.meta.pageTransition && typeof to.meta.pageTransition !== 'boolean') {
    if(backNavSlug === toSlug){
      to.meta.pageTransition.name = 'slide-fade-page-back'
    } else {
      to.meta.pageTransition.name = 'slide-fade-page'
    }
  }
})
